import React, { useEffect, useRef, useState } from "react";
import {
  Droplets, Zap, Shield, Settings, ChevronRight, Phone,
  Mail, MapPin, ArrowRight, CheckCircle, Gauge, Factory,
  Wrench, SprayCan, Waves, Menu, X, Clock, Users,
  Cog, Monitor, Loader2 } from
"lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Analytics } from "@vercel/analytics/react";
import axios from "axios";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ===== Reveal on scroll hook ===== */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function RevealSection({ children, className = "", delay = 0 }) {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>

      {children}
    </div>);

}

/* ===== NAVIGATION ===== */
function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
  { href: "#o-nama", label: "O nama" },
  { href: "#proizvodi", label: "Proizvodi" },
  { href: "#prednosti", label: "Prednosti" },
  { href: "#proces", label: "Proces" },
  { href: "#kontakt", label: "Kontakt" }];


  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ?
      "backdrop-blur-xl bg-zinc-950/80 border-b border-zinc-800" :
      "bg-transparent"}`
      }>

      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <a
          href="#hero"
          data-testid="nav-logo"
          className="font-heading text-xl md:text-2xl font-semibold tracking-tight text-white">

          INTERMETAL
        </a>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {links.map((l) =>
          <a
            key={l.href}
            href={l.href}
            data-testid={`nav-link-${l.href.slice(1)}`}
            className="nav-link text-sm text-zinc-400 font-medium tracking-wide uppercase">

              {l.label}
            </a>
          )}
          <a href="#kontakt">
            <Button
              data-testid="nav-cta-button"
              className="bg-sky-500 text-white hover:bg-sky-600 rounded-sm text-sm font-semibold tracking-wide btn-glow">

              Zatrazite ponudu
            </Button>
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-zinc-400"
          onClick={() => setMenuOpen(!menuOpen)}>

          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen &&
      <div
        data-testid="mobile-menu"
        className="md:hidden bg-zinc-950/95 backdrop-blur-xl border-b border-zinc-800 px-6 pb-6 animate-fade-in">

          {links.map((l) =>
        <a
          key={l.href}
          href={l.href}
          onClick={() => setMenuOpen(false)}
          className="block py-3 text-zinc-300 text-sm uppercase tracking-wide border-b border-zinc-800/50">

              {l.label}
            </a>
        )}
          <a href="#kontakt" onClick={() => setMenuOpen(false)}>
            <Button className="mt-4 w-full bg-sky-500 text-white hover:bg-sky-600 rounded-sm text-sm font-semibold">
              Zatrazite ponudu
            </Button>
          </a>
        </div>
      }
    </nav>);

}

/* ===== HERO SECTION ===== */
function HeroSection() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center">

      {/* BG Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1762506168937-fb21f3fcad84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxjYXIlMjB3YXNoJTIwc3RhdGlvbnxlbnwwfHx8fDE3NzQ4MTM3NTF8MA&ixlib=rb-4.1.0&q=85"
          alt="Auto perionica"
          className="w-full h-full object-cover" />

        <div className="hero-overlay absolute inset-0" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24">
        <div className="max-w-3xl">
          <Badge
            data-testid="hero-badge"
            className="mb-6 bg-sky-500/10 text-sky-400 border-sky-500/20 rounded-sm uppercase text-xs tracking-[0.2em] font-semibold px-3 py-1 animate-fade-in">

            Intermetal Kragujevac
          </Badge>

          <h1
            data-testid="hero-heading"
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter text-white leading-[1.05] animate-fade-in-up">

            Profesionalni sistemi za
            <span className="text-sky-500"> auto-perionice</span>
          </h1>

          <p
            data-testid="hero-subheading"
            className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl animate-fade-in-up delay-200 opacity-0"
            style={{ animationFillMode: "forwards" }}>Projektujemo i proizvodimo sisteme za samouslužne i automatske beskontaktne perionice. Radni pritisak 120-150 bari, motori italijanskih proizvođača.




          </p>

          <div
            className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-400 opacity-0"
            style={{ animationFillMode: "forwards" }}>

            <a href="#kontakt">
              <Button
                data-testid="hero-cta-button"
                className="bg-sky-500 text-white hover:bg-sky-600 rounded-sm h-12 px-8 text-sm font-semibold tracking-wide btn-glow"
                size="lg">

                Zatrazite ponudu
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </a>
            <a href="#proizvodi">
              <Button
                data-testid="hero-secondary-button"
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-sm h-12 px-8 text-sm font-semibold tracking-wide"
                size="lg">

                Pogledajte proizvode
              </Button>
            </a>
          </div>

          {/* Stats */}
          <div
            className="mt-16 grid grid-cols-3 gap-8 max-w-md animate-fade-in-up delay-600 opacity-0"
            style={{ animationFillMode: "forwards" }}>

            <div>
              <div
                data-testid="hero-stat-pressure"
                className="font-heading text-3xl font-semibold text-white">120-150


              </div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">barI pritisak

              </div>
            </div>
            <div>
              <div
                data-testid="hero-stat-products"
                className="font-heading text-3xl font-semibold text-white">

                2
              </div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">
                tipa sistema
              </div>
            </div>
            <div>
              <div
                data-testid="hero-stat-quality"
                className="font-heading text-3xl font-semibold text-white">

                IT
              </div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">
                motori
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

/* ===== ABOUT SECTION ===== */
function AboutSection() {
  const values = [
  {
    icon: Gauge,
    title: "Visok pritisak",
    desc: "Radni pritisak od 120 do 150 bara osigurava temeljno \u010Di\u0161\u0107enje svakog vozila."
  },
  {
    icon: Factory,
    title: "Italijanski motori",
    desc: "Koristimo motore renomiranih italijanskih proizvo\u0111a\u010Da za maksimalnu pouzdanost."
  },
  {
    icon: Shield,
    title: "Premijum kvalitet",
    desc: "Pocinkovana \u010Deli\u010Dna konstrukcija od najkvalitetnijih materijala za dugotrajnost."
  },
  {
    icon: Settings,
    title: "Prilagodljivost",
    desc: "Svaki sistem se mo\u017Ee konfigurisati prema specifi\u010Dnim potrebama klijenta."
  }];


  return (
    <section
      id="o-nama"
      data-testid="about-section"
      className="py-24 md:py-32 px-6 md:px-12">

      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Left - Image */}
          <RevealSection>
            <div className="img-zoom rounded-sm overflow-hidden border border-zinc-800">
              <img
                src="/about-company.jpg"
                alt="Industrijska oprema"
                className="w-full h-80 md:h-[480px] object-cover" />

            </div>
          </RevealSection>

          {/* Right - Content */}
          <div>
            <RevealSection>
              <p className="uppercase text-xs tracking-[0.2em] font-semibold text-sky-500 mb-4">
                O kompaniji
              </p>
              <h2
                data-testid="about-heading"
                className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white">

                Gradimo pouzdane sisteme za pranje vozila
              </h2>
              <p className="mt-4 text-base text-zinc-400 leading-relaxed">Intermetal Kragujevac je specijalizovana kompanija za projektovanje i proizvodnju profesionalnih sistema za auto-perionice. Sa dugogodišnjim iskustvom, nudimo rešenja vrhunskog kvaliteta koja zadovoljavaju najstrožije standarde industrije.





              </p>
            </RevealSection>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {values.map((v, i) =>
              <RevealSection key={v.title} delay={i * 100}>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-sky-500/10 border border-sky-500/20 rounded-sm flex items-center justify-center">
                      <v.icon size={18} className="text-sky-500" />
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-white tracking-tight">
                        {v.title}
                      </h3>
                      <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                        {v.desc}
                      </p>
                    </div>
                  </div>
                </RevealSection>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}

/* ===== PRODUCTS SECTION ===== */
function ProductsSection() {
  const products = [
  {
    id: "self-service",
    title: "Self service car wash system",
    subtitle: "SAmouslu\u017Ene perionice",
    desc: "Potpuno opremljene stanice za samoposlu\u017Eivanje sa visokim pritiskom. Mogu\u0107nost konfigurisanja bokseva po Va\u0161im \u017Eeljama.",
    image:
    "https://customer-assets.emergentagent.com/job_carwash-systems/artifacts/rkn07zeg_samousluzna%20perionica.jpg",
    features: [
    "Visok radni pritisak",
    "Veliki izbor programa",
    "Dug radni vek",
    "Evropski delovi"],

    upgrades: ["Aktivna pena", "Voskiranje", "Osmoza", "Omseksavanje vode"]
  },
  {
    id: "automatic",
    title: "Touchless car wash system",
    subtitle: "AUTOMATSKE BESKONTAKTNE PERIONICE",
    desc: "Automatski sistemi za beskontaktno pranje bez mehanickog kontakta sa vozilom. Brzo, efikasno i bezbedno pranje za svaki tip automobila.",
    image:
    "https://customer-assets.emergentagent.com/job_carwash-systems/artifacts/vf9i8vow_automatska%20perionica.jpg",
    features: [
    "Beskontaktna tehnologija",
    "Potpuno automatski rad",
    "Dug radni vek",
    "Evropski delovi"],

    upgrades: ["Dvostruki šampon dozator", "Aktivna pena", "Voskiranje", "Osmoza", "Omseksavanje vode", "Podno pranje"]
  }];


  return (
    <section
      id="proizvodi"
      data-testid="products-section"
      className="py-24 md:py-32 px-6 md:px-12 bg-zinc-950/50">

      <div className="max-w-7xl mx-auto">
        <RevealSection className="text-center mb-16">
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-sky-500 mb-4">
            Nasi proizvodi
          </p>
          <h2
            data-testid="products-heading"
            className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white">

            Dva tipa sistema za auto-perionice
          </h2>
          <p className="mt-4 text-base text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Nudimo sisteme za samoposluživanje i automatske beskontaktne
            perionice, sa mogucnoscu konfiguracije prema vasim potrebama.
          </p>
        </RevealSection>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {products.map((p, idx) =>
          <RevealSection key={p.id} delay={idx * 150}>
              <div
              data-testid={`product-card-${p.id}`}
              className="bg-zinc-900/50 border border-zinc-800 rounded-sm overflow-hidden card-hover group">

                {/* Image */}
                <div className="img-zoom h-64 md:h-72">
                  <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover" />

                </div>

                {/* Content */}
                <div className="p-8">
                  <Badge className="bg-sky-500/10 text-sky-400 border-sky-500/20 rounded-sm text-xs uppercase tracking-wider mb-4">
                    {p.subtitle}
                  </Badge>
                  <h3 className="font-heading text-2xl font-medium tracking-tight text-white">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                    {p.desc}
                  </p>

                  {/* Features */}
                  <div className="mt-6 space-y-2">
                    {p.features.map((f) =>
                  <div key={f} className="flex items-center gap-2">
                        <CheckCircle
                      size={14}
                      className="text-sky-500 flex-shrink-0" />

                        <span className="text-sm text-zinc-300">{f}</span>
                      </div>
                  )}
                  </div>

                  <Separator className="my-6 bg-zinc-800" />

                  {/* Upgrades */}
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">
                    Dostupne nadogradnje:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {p.upgrades.map((u) =>
                  <span
                    key={u}
                    className="text-xs px-3 py-1 bg-zinc-800/80 border border-zinc-700/50 text-zinc-400 rounded-sm">

                        {u}
                      </span>
                  )}
                  </div>

                  <a href="#kontakt" className="block mt-6">
                    <Button
                    data-testid={`product-cta-${p.id}`}
                    variant="outline"
                    className="w-full border-zinc-700 text-zinc-300 hover:bg-sky-500 hover:text-white hover:border-sky-500 rounded-sm transition-all text-sm font-semibold">

                      Zatrazite ponudu
                      <ChevronRight size={14} className="ml-1" />
                    </Button>
                  </a>
                </div>
              </div>
            </RevealSection>
          )}
        </div>
      </div>
    </section>);

}

/* ===== OPTIONAL FEATURES SECTION ===== */
function FeaturesSection() {
  const features = [
  {
    icon: SprayCan,
    name: "Dvostruki \u0161ampon dozator",
    desc: "Dva nezavisna dozatora za razlicite tipove sampona.",
    for: "Samoposluživanje"
  },
  {
    icon: Droplets,
    name: "Voskiranje",
    desc: "Sistem za nanošenje zaštitnog voska nakon pranja.",
    for: "Oba sistema"
  },
  {
    icon: Waves,
    name: "Osmoza",
    desc: "Sistem za reverznu osmozu za demineralizovanu vodu.",
    for: "Oba sistema"
  },
  {
    icon: Zap,
    name: "Omek\u0161avanje vode",
    desc: "Uklanjanje minerala za cist završni ispiranjem bez fleka.",
    for: "Oba sistema"
  },
  {
    icon: Wrench,
    name: "Podno pranje vozila",
    desc: "Mlaznice za temeljno ciscenje donjeg dela vozila.",
    for: "Automatska"
  },
  {
    icon: Cog,
    name: "Konfigurisanje po meri",
    desc: "Mogucnost prilagodzavanja sistema prema specificnim zahtevima.",
    for: "Oba sistema"
  }];


  return (
    <section
      id="opcije"
      data-testid="features-section"
      className="py-24 md:py-32 px-6 md:px-12">

      <div className="max-w-7xl mx-auto">
        <RevealSection className="text-center mb-16">
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-sky-500 mb-4">
            Dodatne opcije
          </p>
          <h2
            data-testid="features-heading"
            className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white">

            Nadogradite svoj sistem
          </h2>
          <p className="mt-4 text-base text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Svaki nas sistem moze biti opremljen dodatnim opcijama za
            maksimalnu efikasnost i kvalitet pranja.
          </p>
        </RevealSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) =>
          <RevealSection key={f.name} delay={i * 80}>
              <div
              data-testid={`feature-card-${i}`}
              className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-sm card-hover group">

                <div className="w-11 h-11 bg-sky-500/10 border border-sky-500/20 rounded-sm flex items-center justify-center mb-4 feature-icon group-hover:bg-sky-500/20">
                  <f.icon size={20} className="text-sky-500" />
                </div>
                <h3 className="font-heading text-base font-semibold text-white tracking-tight">
                  {f.name}
                </h3>
                <p className="mt-2 text-sm text-zinc-500 leading-relaxed">
                  {f.desc}
                </p>
                <span className="inline-block mt-3 text-xs text-sky-500/80 uppercase tracking-wider">
                  {f.for}
                </span>
              </div>
            </RevealSection>
          )}
        </div>
      </div>
    </section>);

}

/* ===== BENEFITS SECTION ===== */
function BenefitsSection() {
  const benefits = [
  {
    icon: Shield,
    title: "Preko 15 godina iskustva",
    desc: "Profesionalna konstrukcija i vrhunski materijali garantuju dugotrajnost i minimalno odr\u017Eavanje."
  },
  {
    icon: Gauge,
    title: "Performanse",
    desc: "Radni pritisak 120-150 bar osigurava efikasno \u010Di\u0161\u0107enje svakog tipa vozila."
  },
  {
    icon: Factory,
    title: "Italijanski motori",
    desc: "Motori od renomiranih italijanskih proizvo\u0111a\u010Da za vrhunske performanse."
  },
  {
    icon: Settings,
    title: "Prilagodljivost",
    desc: "Modularni sistemi koji se mogu konfigurisati prema Vasim specifi\u010Dnim potrebama."
  },
  {
    icon: Users,
    title: "Podr\u0161ka",
    desc: "Stru\u010Dna tehni\u010Dka podr\u0161ka i servis za besprekoran rad Va\u0161eg sistema."
  },
  {
    icon: Zap,
    title: "Efikasnost",
    desc: "Optimizovana potro\u0161nja vode i energije za ekonomi\u010Dan rad."
  }];


  return (
    <section
      id="prednosti"
      data-testid="benefits-section"
      className="py-24 md:py-32 px-6 md:px-12 bg-zinc-950/50">

      <div className="max-w-7xl mx-auto">
        <RevealSection className="text-center mb-16">
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-sky-500 mb-4">
            Prednosti
          </p>
          <h2
            data-testid="benefits-heading"
            className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white">

            Zasto izabrati Intermetal?
          </h2>
        </RevealSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((b, i) =>
          <RevealSection key={b.title} delay={i * 80}>
              <div
              data-testid={`benefit-card-${i}`}
              className="text-center p-8 group">

                <div className="w-14 h-14 mx-auto bg-sky-500/10 border border-sky-500/20 rounded-sm flex items-center justify-center mb-5 feature-icon group-hover:bg-sky-500/20 transition-colors">
                  <b.icon size={24} className="text-sky-500" />
                </div>
                <h3 className="font-heading text-lg font-medium tracking-tight text-white">
                  {b.title}
                </h3>
                <p className="mt-2 text-sm text-zinc-500 leading-relaxed">
                  {b.desc}
                </p>
              </div>
            </RevealSection>
          )}
        </div>
      </div>
    </section>);

}

/* ===== PROCESS SECTION ===== */
function ProcessSection() {
  const steps = [
  {
    num: "01",
    title: "Konsultacija",
    desc: "Analiziramo vase potrebe i lokaciju za optimalno resenje.",
    icon: Users
  },
  {
    num: "02",
    title: "Konfiguracija",
    desc: "Projektujemo sistem sa opcijama prilago\u0111enim Va\u0161im zahtevima.",
    icon: Cog
  },
  {
    num: "03",
    title: "Proizvodnja",
    desc: "Izra\u0111ujemo sistem po najvi\u0161im standardima kvaliteta.",
    icon: Factory
  },
  {
    num: "04",
    title: "Isporuka i monta\u017Ea",
    desc: "Vr\u0161imo profesionalnu monta\u017Eu i pu\u0161tanje sistema u rad.",
    icon: Wrench
  }];


  return (
    <section
      id="proces"
      data-testid="process-section"
      className="py-24 md:py-32 px-6 md:px-12">

      <div className="max-w-7xl mx-auto">
        <RevealSection className="text-center mb-16">
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-sky-500 mb-4">
            Kako radimo
          </p>
          <h2
            data-testid="process-heading"
            className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white">

            Od ideje do realizacije
          </h2>
        </RevealSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((s, i) =>
          <RevealSection key={s.num} delay={i * 120}>
              <div
              data-testid={`process-step-${i}`}
              className="relative text-center group">

                {/* Number */}
                <div className="font-heading text-5xl font-bold text-zinc-800 group-hover:text-zinc-700 transition-colors">
                  {s.num}
                </div>
                {/* Icon */}
                <div className="w-12 h-12 mx-auto mt-4 bg-sky-500/10 border border-sky-500/20 rounded-sm flex items-center justify-center feature-icon group-hover:bg-sky-500/20 transition-colors">
                  <s.icon size={20} className="text-sky-500" />
                </div>
                <h3 className="font-heading text-lg font-medium tracking-tight text-white mt-4">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-zinc-500 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </RevealSection>
          )}
        </div>
      </div>
    </section>);

}

/* ===== CONFIGURATOR TEASER ===== */
function ConfiguratorTeaser() {
  return (
    <section
      id="konfigurator"
      data-testid="configurator-section"
      className="py-24 md:py-32 px-6 md:px-12 bg-zinc-950/50">

      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <div className="relative overflow-hidden border border-zinc-800 rounded-sm p-10 md:p-16 text-center bg-zinc-900/30">
            {/* Decorative grid */}
            <div className="absolute inset-0 opacity-5">
              <div
                className="w-full h-full"
                style={{
                  backgroundImage:
                  "linear-gradient(rgba(14,165,233,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(14,165,233,0.3) 1px, transparent 1px)",
                  backgroundSize: "40px 40px"
                }} />

            </div>

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-sky-500/10 border border-sky-500/20 rounded-sm px-4 py-2 mb-6">
                <Monitor size={16} className="text-sky-500" />
                <span className="text-xs text-sky-400 uppercase tracking-[0.2em] font-semibold">
                  Uskoro dostupno
                </span>
              </div>

              <h2
                data-testid="configurator-heading"
                className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white">

                Online konfigurator proizvoda
              </h2>
              <p className="mt-4 text-base text-zinc-400 max-w-xl mx-auto leading-relaxed">
                Radimo na interaktivnom konfiguratoru koji ce vam omoguciti da
                samostalno izaberete opcije i kreirate ponudu za vas sistem.
              </p>
              <div className="mt-8 flex items-center justify-center gap-3">
                <Clock size={16} className="text-zinc-600" />
                <span className="text-sm text-zinc-600">U pripremi</span>
              </div>
            </div>
          </div>
        </RevealSection>
      </div>
    </section>);

}

/* ===== CONTACT SECTION ===== */
function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", system_type: "", message: ""
  });
  const [honeypot, setHoneypot] = useState("");
  const [formLoadedAt] = useState(Date.now());

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Honeypot check — bots fill hidden fields
    if (honeypot) return;

    // Time check — reject if submitted in under 3 seconds (bot speed)
    if (Date.now() - formLoadedAt < 3000) {
      setError("Molimo sačekajte pre slanja forme.");
      return;
    }

    if (!formData.name || !formData.email) {
      setError("Molimo unesite ime i email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        _hp: honeypot,
        _ts: formLoadedAt
      });
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", system_type: "", message: "" });
    } catch (err) {
      const msg = err.response?.data?.detail;
      setError(msg || "Došlo je do greške. Pokušajte ponovo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-24 md:py-32 px-6 md:px-12">

      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20">
          {/* Left info */}
          <RevealSection>
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-sky-500 mb-4">
              Kontakt
            </p>
            <h2
              data-testid="contact-heading"
              className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white">Zatražite ponudu


            </h2>
            <p className="mt-4 text-base text-zinc-400 leading-relaxed">Popunite formular i naš tim će vas kontaktirati u najkraćem mogućem roku sa detaljnom ponudom prilagođenom Vašim potrebama.


            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 rounded-sm flex items-center justify-center">
                  <Phone size={16} className="text-sky-500" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Telefon</p>
                  <p
                    data-testid="contact-phone"
                    className="text-sm text-white">

                    +381 65 315 62 00 <span className="text-zinc-500">Kragujevac</span>
                  </p>
                  <p
                    data-testid="contact-phone-bg"
                    className="text-sm text-white">

                    +381 60 301 6070 <span className="text-zinc-500">Beograd</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 rounded-sm flex items-center justify-center">
                  <Mail size={16} className="text-sky-500" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Email</p>
                  <p
                    data-testid="contact-email"
                    className="text-sm text-white">info@automatskeperionice.rs


                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 rounded-sm flex items-center justify-center">
                  <MapPin size={16} className="text-sky-500" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Adresa</p>
                  <p
                    data-testid="contact-address"
                    className="text-sm text-white">Igumana Pajsija 3, 34000 Kragujevac


                  </p>
                </div>
              </div>
            </div>
          </RevealSection>

          {/* Right form */}
          <RevealSection delay={150}>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="space-y-5 bg-zinc-900/50 border border-zinc-800 rounded-sm p-8">

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">
                    Ime i prezime
                  </label>
                  <Input
                    data-testid="contact-input-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Vase ime"
                    className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600 rounded-sm focus-visible:ring-sky-500" />

                </div>
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">
                    Email
                  </label>
                  <Input
                    data-testid="contact-input-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="vas@email.com"
                    className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600 rounded-sm focus-visible:ring-sky-500" />

                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">
                  Telefon
                </label>
                <Input
                  data-testid="contact-input-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+381 ..."
                  className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600 rounded-sm focus-visible:ring-sky-500" />

              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">
                  Tip sistema
                </label>
                <select
                  data-testid="contact-select-system"
                  name="system_type"
                  value={formData.system_type}
                  onChange={handleChange}
                  className="flex h-9 w-full rounded-sm border border-zinc-700 bg-zinc-900 px-3 py-1 text-sm text-white shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sky-500">

                  <option value="">Izaberite tip sistema</option>
                  <option value="self-service">
                    Samoposluživanje
                  </option>
                  <option value="automatic">
                    Automatska beskontaktna
                  </option>
                  <option value="both">Oba sistema</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">
                  Poruka
                </label>
                <Textarea
                  data-testid="contact-input-message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Opisite vase potrebe..."
                  className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600 rounded-sm resize-none focus-visible:ring-sky-500" />

              </div>

              {/* Honeypot - hidden from humans, visible to bots */}
              <div className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10 overflow-hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off" />

              </div>

              <Button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="w-full bg-sky-500 text-white hover:bg-sky-600 rounded-sm h-11 text-sm font-semibold tracking-wide btn-glow disabled:opacity-50">

                {loading ?
                <><Loader2 size={14} className="mr-2 animate-spin" /> Slanje...</> :
                submitted ? "Poruka poslata!" :
                <>Posaljite upit <ArrowRight size={14} className="ml-2" /></>
                }
              </Button>

              {error &&
              <p data-testid="contact-error-message" className="text-sm text-red-400 text-center animate-fade-in">
                  {error}
                </p>
              }

              {submitted &&
              <p
                data-testid="contact-success-message"
                className="text-sm text-sky-400 text-center animate-fade-in">

                  Hvala na interesovanju! Kontaktiraćemo vas uskoro.
                </p>
              }
            </form>
          </RevealSection>
        </div>
      </div>
    </section>);

}

/* ===== FOOTER ===== */
function Footer() {
  return (
    <footer
      data-testid="footer"
      className="border-t border-zinc-800 py-12 px-6 md:px-12">

      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <span className="font-heading text-lg font-semibold tracking-tight text-white">
            INTERMETAL
          </span>
          <p className="text-xs text-zinc-600 mt-1">Kragujevac, Srbija</p>
        </div>
        <div className="flex items-center gap-8">
          <a
            href="#o-nama"
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors uppercase tracking-wider">

            O nama
          </a>
          <a
            href="#proizvodi"
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors uppercase tracking-wider">

            Proizvodi
          </a>
          <a
            href="#kontakt"
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors uppercase tracking-wider">

            Kontakt
          </a>
        </div>
        <p className="text-xs text-zinc-700">
          &copy; 2025 Intermetal Kragujevac. Sva prava zadrzana.
        </p>
      </div>
    </footer>);

}

/* ===== MAIN APP ===== */
function App() {
  return (
    <div className="noise-overlay min-h-screen bg-[#09090b]">
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <ProductsSection />
        <FeaturesSection />
        <BenefitsSection />
        <ProcessSection />
        <ConfiguratorTeaser />
        <ContactSection />
      </main>
      <Footer />
      <Analytics />
    </div>);

}

export default App;
